// Director calls are charged per minute (v3, api/src/director/billing.ts). Pure helpers for the call meter, the
// wrap-up before a call runs out, and the copy when a call can't start. Unit-tested in callmeter.test.ts.

/** Seconds before the end when the Director is told to wrap up. */
export const WRAP_UP_S = 30;

/** What a call has cost so far, as the api bills it (per second, rounded up to whole credits). */
export function callCredits(seconds: number, creditsPerMin: number): number {
  return Math.max(0, Math.ceil((Math.max(0, seconds) * creditsPerMin) / 60 - 1e-9));
}

/** "0:42", "12:05". */
export function fmtClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** When to tell the Director to wrap up, and when to end, in ms after connect (null: no limit). */
export function callTimers(maxSeconds: number | null | undefined): { wrapMs: number; endMs: number } | null {
  if (!maxSeconds || maxSeconds <= 0) return null;
  return { wrapMs: Math.max(0, maxSeconds - WRAP_UP_S) * 1000, endMs: maxSeconds * 1000 };
}

/** v4 rolling hold: how often the web ticks an open call (the api charges the minute and holds the next 5). */
export const TICK_EVERY_S = 60;

/**
 * v4: where the call ends after a tick, in seconds from connect: the tick's call time plus the seconds the api says
 * are left. A tick that didn't answer (null) keeps the old limit (a missed tick just waits for the next one).
 */
export function limitAfterTick(prevLimitS: number | null, tickAtS: number, secondsLeft: number | null | undefined): number | null {
  if (secondsLeft === null || secondsLeft === undefined || !Number.isFinite(secondsLeft)) return prevLimitS;
  return Math.max(0, tickAtS) + Math.max(0, secondsLeft);
}

/** v4: the wrap-up and end timers for a (moved) limit, in ms from now, when the call is `elapsedS` in. */
export function callTimersFrom(limitS: number | null | undefined, elapsedS: number): { wrapInMs: number; endInMs: number } | null {
  const t = callTimers(limitS);
  if (!t) return null;
  const now = Math.max(0, elapsedS) * 1000;
  return { wrapInMs: Math.max(0, t.wrapMs - now), endInMs: Math.max(0, t.endMs - now) };
}

/** Long calls: every this many seconds the Director asks whether they'd like to keep going (instead of a hard stop). */
export const CHECK_IN_EVERY_S = 20 * 60;

/** When to check in (ms from the start): every 20 min, but not in the last 2 minutes before the call's own limit. */
export function checkInTimes(maxSeconds: number | null | undefined, hardCapS = 3600): number[] {
  const end = maxSeconds && maxSeconds > 0 ? Math.min(maxSeconds, hardCapS) : hardCapS;
  const out: number[] = [];
  for (let t = CHECK_IN_EVERY_S; t < end - 120; t += CHECK_IN_EVERY_S) out.push(t * 1000);
  return out;
}

/** What the Director hears at a check-in. */
export const checkInLine = (minutes: number) =>
  `[app] You've been on this call about ${minutes} minutes. At a natural pause (not mid-step, not while they're answering), ask once, briefly, whether they'd like to keep going. If they say no or that they're done, say a short goodbye and call end_call. If yes, carry on. If they don't answer, keep going.`;

/** What the Director hears ~30 s before the call's credits run out. */
export const WRAP_UP_LINE = "[app] This call is nearly out of time: about 30 seconds left on the credits for it. Say one short, warm wrap-up line now (where you left off, and that you can pick up next call), then stop talking.";

/**
 * Wave 3b: what the Director hears ~30 s before the free tour minutes run out with no credits to carry on (the app
 * opens the credit packs when the call ends; tour.ts freeTimeEnding decides which line goes).
 */
export const FREE_WRAP_UP_LINE =
  "[app] The free tour minutes are nearly used up: about 30 seconds left, and there aren't credits to keep talking. Say once, warmly, that the three free minutes are used up, that the credit packs are opening on screen, and that they can keep going by tapping, which is free. Then stop talking.";
/** Wave 3b: the studio line when the free tour minutes ended the call (the packs open with it). */
export const FREE_ENDED_LINE = "Your three free Director minutes are used up. Pick a credit pack to keep talking; tapping through the steps stays free.";
/** The wrap-up line for a call: the free-minutes one when that's what's ending it. */
export const wrapUpLine = (freeEnding: boolean) => (freeEnding ? FREE_WRAP_UP_LINE : WRAP_UP_LINE);

/** 2026-11-02: a call that failed to connect, or dropped within QUICK_FAIL_MS: no automatic retry, and this line. */
export const CANT_CONNECT_LINE = "The Director can't connect right now. Try again in a minute; everything on screen still works by tapping.";
export const QUICK_FAIL_MS = 8000;
/** The studio line after a call failed to connect (a provider out of quota gets the outage line). */
export function cantConnectLine(error: string | null | undefined): string {
  return error && /quota|out of credits/i.test(error)
    ? "The Director can't take calls right now. We've been told and we're fixing it. Everything on screen still works."
    : CANT_CONNECT_LINE;
}

/** The studio line when the app ends a call at its limit. */
export function endedLine(balance: number | null, creditsPerMin: number): string {
  return balance !== null && balance < creditsPerMin
    ? "The call ended: it used the credits you had. Top up to talk again. Everything on screen still works by tapping."
    : "The call reached its time limit. Tap the light to start another; everything on screen still works by tapping.";
}

/** The state word when a call can't start for a known reason (not a fault: shown calmly, not as an error). */
export function limitWord(code: string | null): string | null {
  return code === "NO_CREDITS" ? "Top up to talk" : code === "DIRECTOR_DAILY_LIMIT" ? "Back tomorrow" : code === "DIRECTOR_BUSY" || code === "THROTTLED" ? "Busy right now" : code === "DIRECTOR_UNAVAILABLE" ? "Unavailable" : null;
}

/** The copy (and whether to offer a top-up) when a call can't start. */
export function startProblem(code: string | null, message: string, creditsPerMin: number): { text: string; topUp: boolean } {
  switch (code) {
    case "NO_CREDITS":
      return { text: `Talking to the Director costs ${creditsPerMin} credits a minute, and your balance is below that. Top up to start a call. You can keep working on screen.`, topUp: true };
    case "DIRECTOR_DAILY_LIMIT":
      return { text: message || "That's all the Director time for today. You can keep working on screen, and the Director is back tomorrow.", topUp: false };
    case "DIRECTOR_BUSY":
      return { text: "The Director is very busy right now. You can keep working on screen; try a call again a little later.", topUp: false };
    case "DIRECTOR_UNAVAILABLE":
      return { text: message || CANT_CONNECT_LINE, topUp: false };
    case "THROTTLED":
      return { text: "That's a lot of calls this hour. Give it a few minutes, then try again. Everything on screen still works.", topUp: false };
    default:
      return { text: `Couldn't reach the Director${message ? `: ${message}` : ""}. Everything still works by tapping.`, topUp: false };
  }
}
